<template>
  <div>
    <select
      v-model="innerValue"
      :disabled="disabled"
      class="ui-base-select"
    >
      <option
        v-for="e in optionValue"
        :title="innerDescriptionFunction(e)"
        :value="innerValueFunction(e)"
        :key="innerValueFunction(e)"
      >
        {{ innerLabelFunction(e) }}
      </option>
    </select>
  </div>
</template>

<script lang="ts" setup>
const { ls } = useI18n()

interface Props {
  mandatory?: boolean
  disabled?: boolean
  labelFunction?: Function
  valueFunction?: Function
  descriptionFunction?: Function
  modelValue: any
  options?: any[]
  enum?: EnumDef
  code?: Code
}
const props = withDefaults(defineProps<Props>(), {
  mandatory: true,
  disabled: false,
  labelFunction: (v) => v,
  valueFunction: (v) => v,
  descriptionFunction: (v) => null,
  options: () => []
})
const emits = defineEmits(['update:modelValue'])

const optionValue = computed(() => {
  if (props.code) {
    const values = props.code.values.map(c => c.code)
    if (!props.mandatory) values.unshift(null)
    return values
  } else if (props.enum) {
    const values = Object.keys(props.enum.c)
    if (!props.mandatory) values.unshift(null)
    return values
  } else {
    return props.options
  }
})

const innerValueFunction = computed(() => {
  if (props.enum || props.code) {
    return (e) => e
  } else {
    return props.valueFunction
  }
})

const innerLabelFunction = computed(() => {
  if (props.code) {
    return (c) => {
      if (c) return ls(props.code.values.find(cc => cc.code === c)?.value)
      else return ''
    }
  } else if (props.enum) {
    return (e) => {
      if (e) return ls(props.enum.c[e].l)
      else return ''
    }
  } else {
    return props.labelFunction
  }
})

const innerDescriptionFunction = computed(() => {
  if (props.enum) {
    return (e) => {
      if (e) return ls(props.enum.c[e].d)
      else return ''
    }
  } else if (props.code) {
    return (c) => {
      if (c) return ls(props.code.values.find(cc => cc.code === c)?.description)
      else return ''
    }
  } else {
    return props.descriptionFunction
  }
})

const innerValue = computed({
  get: () => props.modelValue,
  set: (v) => {
    emits('update:modelValue', v)
  },
})
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.ui-base-select {
  display: block;
  border-radius: 0.25rem;
  border: 1px solid $jps-gray-300;
  padding: 0.125rem;
  background-color: $white;

  &:focus {
    border-color: $jps-key-blue;
  }
  &:focus {
    box-shadow: 0 0 0 1px $jps-key-blue;
  }
}
</style>
